import * as prod from 'react/jsx-runtime';
import React from 'react';
import { useRemark } from 'react-remark';
import remarkDefList from 'remark-deflist';
import rehypeRaw from 'rehype-raw';
import { FaqAnswer, FaqQuestion, FaqSection } from '@features/markdown/components/FAQ';
import { KlaviyoForm } from './components/KlaviyoForm';
import { rehypePlaceholders, Placeholder } from '@features/markdown/placeholderPlugin';
import { rehypeIcons } from '@features/markdown/iconPlugin';
import { rehypeUtil } from '@features/markdown/utilPlugin';
import { Button, Anchor } from '@components/tailwind';

const rehypeReactOptions = (placeholders, unwrapParagraphs = false) => ({
  Fragment: prod.Fragment,
  jsx: prod.jsx,
  jsxs: prod.jsxs,
  components: {
    /**
     * Register custom components here:
     */
    dl: FaqSection,
    dt: FaqQuestion,
    dd: FaqAnswer,
    'klaviyo-form': KlaviyoForm,
    placeholder: Placeholder(placeholders),
    button: Button,
    a: Anchor,
    ...(unwrapParagraphs ? { p: ({ children }) => children } : {}),
  },
});

/**
 * Helper hook for rendering markdown components as JSX
 *
 * Usage:
 * ```tsx
 * const [content, setSource] = useMarkdown();
 *
 * return (
 *   <>
 *     <input type="text" onChange={({ currentTarget }) => setSource(currentTarget.value)} />
 *     <div className="tw:prose">
 *       {content}
 *     </div>
 *   </>
 * )
 * ```
 */
export function useMarkdown({ placeholders = {}, unwrapParagraphs = false } = {}) {
  // @ts-ignore
  const [content, _setSource] = useRemark({
    remarkPlugins: [remarkDefList],
    remarkToRehypeOptions: {
      allowDangerousHtml: true,
    },
    rehypePlugins: [
      // @ts-expect-error: TODO: fix react types
      rehypeRaw,
      rehypePlaceholders,
      rehypeIcons,
      rehypeUtil,
    ],
    // @ts-expect-error: TODO: fix react types
    rehypeReactOptions: rehypeReactOptions(placeholders, unwrapParagraphs),
  });

  const setSource = (s) => _setSource((s || "").replaceAll(/\btw-/g, "tw:"))

  return [content, setSource] as const;
}
